import React, { useEffect, useRef, useState } from 'react';
import { PrivateMeetingDetailsResponseType, PublicMeetingDetailsResponseType } from '../../../../lib/api';
import { AppConfigurator } from '../../../../lib/services/app-configuration/AppConfigurator';
import { JoinMethod } from '../../../../lib/join-method/join-method';
import { Connection } from '../../../../lib/connection/connection';
import DefaultSpinner from '../../../shared/DefaultSpinner';
import ConnectToMeeting, { ZOOM_REGISTRATION_IN_PROGRESS_TEXT } from '../ConnectToMeeting';
import { useMeetingRegistrationSubscription } from '../../../../hooks/use-meeting-registration-subscription';
import { fetchMeetingRegistrationSubscriptionParams, registerToMeeting } from '../../../../lib/api/protected';
import {
  fetchMeetingLinkPanelistPreregister,
  fetchMeetingLinkPanelistProcessingResult,
  registerMeetingLinkPanelist,
} from '../../../../lib/api/public';
import { getOrCreateMeetingLinkDeviceToken } from '../../../../lib/meeting-link-device-token';
import useMeetingLinkPath from '../../../../hooks/use-meeting-link-path';
import useBypassToken from '../../../../hooks/use-bypass-token';
import { ERR_MODAL_COMMON_STYLES, LIST_ALLOWED_CONNECTIONS, PassportNonce } from '../../../../lib/constants';
import { Button } from '../../../../components/_base/Button';
import Modal from 'react-modal';
import { ConnectionVersion } from '../../../../lib/api/connection.types';
import SafeHTMLRender from '../../../shared/SafeHTMLRender';

export type ConnectionContentItemProps = {
  eventId: string;
  meetingDetails: PrivateMeetingDetailsResponseType | PublicMeetingDetailsResponseType;
  isConnectBtnDisabled: boolean;
  joinMethod: JoinMethod | null;
};

export const ConnectionContentItem: React.FunctionComponent<ConnectionContentItemProps> = (props) => {
  const {
    eventId, meetingDetails, isConnectBtnDisabled, joinMethod
  } = props;

  const isPublicMeetingLink = AppConfigurator.isMeetLinksProfile;
  const meetingLinkPath = useMeetingLinkPath();
  const bypassToken = useBypassToken();
  const isMeetingInThePast = Date.now() > Date.parse(meetingDetails.endDateTime);
  const connection = Connection.create(meetingDetails);
  const isDirectlyAssignedConnection = !connection?.isMultipleConnection();
  const isPanelistLinkOverride = isPublicMeetingLink
    && !!meetingDetails.createPanelistLinkEnabled
    && !!meetingDetails.panelistLinkEligible
    && !!joinMethod?.isWebinarAutoSyncEnabled
    && !isMeetingInThePast;
  const isAudioBridgeAutoSync = (!!joinMethod?.isAudioBridgeType() || !!joinMethod?.isUniqueDialInType()) && !!joinMethod?.isRegistrationNeeded;
  const isDirectAutoSyncJoin = isPublicMeetingLink
    && !isMeetingInThePast
    && isDirectlyAssignedConnection
    && !isPanelistLinkOverride
    && (!!joinMethod?.isWebinarAutoSyncEnabled || isAudioBridgeAutoSync);
  const shouldCreatePanelistLink = isPanelistLinkOverride || isDirectAutoSyncJoin;
  const [componentLoading, setComponentLoading] = useState<boolean>(!isPublicMeetingLink || shouldCreatePanelistLink);
  const { state, subscribe, unsubscribe } = useMeetingRegistrationSubscription(() => {
    if (!shouldCreatePanelistLink && !state.inProgress) {
      setComponentLoading(false);
    }
  });
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [showDialInInfo, setShowDialInInfo] = useState<boolean>(false);
  const [isDialInPending, setIsDialInPending] = useState<boolean>(false);
  const [panelistJoinUrl, setPanelistJoinUrl] = useState<string | null>(null);
  const [panelistUniqueDialInCode, setPanelistUniqueDialInCode] = useState<string | null>(null);
  const hasSubscribedRef = useRef<boolean>(false);

  const toggleDialInInfo = (): void => {
    const newValue = !showDialInInfo;
    setShowDialInInfo(newValue);
    if (!isPublicMeetingLink && newValue && isAudioBridgeType && showHideButtonEnabled && isRegistrationNeeded && !hasSubscribedRef.current) {
      setIsDialInPending(true);
    }
  };

  const openErrorModal = () => setIsErrorModalOpen(true);
  const closeErrorModal = () => setIsErrorModalOpen(false);

  const connectionDetails = !meetingDetails.registrationNeeded && (joinMethod ? joinMethod.meetingInvitation : meetingDetails.connectionDetails);
  const isKnovioConnectionPublish = joinMethod?.isKnovioConnectionType() ? joinMethod.isKnovioAvailable : true;
  const isMultipleConnection = connection?.isMultipleConnection() as boolean;
  const isRegistrationNeeded = meetingDetails.registrationNeeded || joinMethod?.isRegistrationNeeded;

  const joinUrl = panelistJoinUrl || state.joinUrl || null;

  const isAudioBridgeType = joinMethod?.isAudioBridgeType() || joinMethod?.isUniqueDialInType();
  /* [OEP-30761] - for now always show the show/hide button for the AudioBrdige connection in group, ignoring the value of showHideButtonEnabled from Central */
  const showHideButtonEnabled = isAudioBridgeType && isMultipleConnection;
  const showDialInInfoButtonLabel = joinMethod?.showDialInInfo;
  const hideDialInInfoButtonLabel = joinMethod?.hideDialInInfo;
  const displayShowDialInInfoButton = `connection-item__dial-in ${showDialInInfo ? 'block' : 'hidden'}`;

  const dialInfoButtonTitle = showDialInInfo ? hideDialInInfoButtonLabel : showDialInInfoButtonLabel;

  const listAllowedConnections = LIST_ALLOWED_CONNECTIONS;
  if (meetingDetails?.connection?.connectionVersion !== ConnectionVersion.V1) {
    listAllowedConnections.concat('UNIQUE_JOINING_LINK');
  }

  const isPassportDisplayConnection = (connectionType: string): boolean => {
    return listAllowedConnections.includes(connectionType);
  };

  const getPassportConfiguration = (): JSX.Element => {
    return (
      <div className='display-configuration'>
        {joinMethod?.passportLabel && (
          <div className='label-block'>
            <div className='label'>{joinMethod.passportLabel}</div>
            <hr />
          </div>
        )}
        {joinMethod?.passportDescription && (
          <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={joinMethod.passportDescription} className='description'/>
        )}
      </div>
    );
  };

  const getSpinner = (className = ''): JSX.Element => {
    return (
      <div className={`${className} bg-white p-8 shadow-gray`}>
        {shouldCreatePanelistLink ? <span className='meeting-link-spinner add-spin animate-spin' /> : <DefaultSpinner visible={true} />}
        <div className='pt-4 whitespace-pre-wrap leading-loose'>{ZOOM_REGISTRATION_IN_PROGRESS_TEXT}</div>
      </div>
    );
  };

  const isConnectionDetailsShown = joinMethod?.type && isPassportDisplayConnection(joinMethod.type)
    ? !!joinMethod?.connectionDetailsEnabled
    : true;

  const preregisterToMeeting = async (): Promise<void> => {
    let registrationId = '';
    const connectionId = connection?.getConnectionId();
    const joinMethodId = joinMethod?.joinMethodId;

    try {
      const {
        registrationRequired,
        ...registrationSubscriptionParams
      } = await fetchMeetingRegistrationSubscriptionParams(eventId, meetingDetails.meetingId, connectionId, joinMethodId);
      registrationId = registrationSubscriptionParams.registrationId;

      await subscribe(registrationSubscriptionParams, joinMethod?.isAudioBridgeType());
      if (registrationRequired) await registerToMeeting(eventId, meetingDetails.meetingId, registrationId, connectionId, joinMethodId);
    } catch (e) {
      if (e?.message === 'ConnectionDisrupted') {
        openErrorModal();
        registerToMeeting(eventId, meetingDetails.meetingId, registrationId, connectionId, joinMethodId);
      }
    }
  };

  const createOrReusePanelist = async (): Promise<void> => {
    if (!meetingLinkPath || !bypassToken) {
      setComponentLoading(false);
      return;
    }

    let registrationId = '';
    let deviceToken = '';
    try {
      deviceToken = getOrCreateMeetingLinkDeviceToken(meetingLinkPath);
      const {
        registrationRequired,
        joinUrl,
        ...registrationSubscriptionParams
      } = await fetchMeetingLinkPanelistPreregister(meetingLinkPath, bypassToken, deviceToken);

      registrationId = registrationSubscriptionParams.registrationId ?? '';
      if (joinUrl && isAudioBridgeType) {
        setPanelistUniqueDialInCode(joinUrl);
      }
      if (joinUrl && !registrationRequired) {
        if (!isAudioBridgeType) {
          setPanelistJoinUrl(joinUrl);
        }
        setComponentLoading(false);
        return;
      }
      unsubscribe();
      await subscribe(
        registrationSubscriptionParams,
        !!joinMethod?.isAudioBridgeType() || !!joinMethod?.isUniqueDialInType(),
        () => fetchMeetingLinkPanelistProcessingResult(meetingLinkPath, bypassToken, registrationId, deviceToken)
      );
      if (registrationRequired) await registerMeetingLinkPanelist(meetingLinkPath, bypassToken, deviceToken, registrationId);
    } catch (e) {
      if (e?.message === 'ConnectionDisrupted' && registrationId) {
        setComponentLoading(false);
        openErrorModal();
        registerMeetingLinkPanelist(meetingLinkPath, bypassToken, deviceToken, registrationId);
        return;
      }
      setComponentLoading(false);
    }
  };

  const shouldDeferToToggle = isAudioBridgeType && showHideButtonEnabled;

  useEffect(() => {
    if (isPublicMeetingLink) {
      if (shouldCreatePanelistLink) {
        createOrReusePanelist();
      } else {
        setComponentLoading(false);
      }
    } else if (isRegistrationNeeded && !isMeetingInThePast) {
      if (!shouldDeferToToggle) {
        preregisterToMeeting();
      } else {
        setComponentLoading(false);
      }
    } else {
      setComponentLoading(false);
    }

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shouldCreatePanelistLink) {
      return;
    }
    if (state.joinUrl || state.uniqueDialInCode || state.error) {
      setComponentLoading(false);
    }
  }, [shouldCreatePanelistLink, state.joinUrl, state.uniqueDialInCode, state.error]);

  useEffect(() => {
    if (isPublicMeetingLink) {
      return;
    }
    if (
      showDialInInfo &&
      shouldDeferToToggle &&
      isRegistrationNeeded &&
      !isMeetingInThePast &&
      !hasSubscribedRef.current
    ) {
      hasSubscribedRef.current = true;
      preregisterToMeeting().finally(() => setIsDialInPending(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialInInfo]);

  if (componentLoading) {
    return shouldCreatePanelistLink ? getSpinner('m-8') : null;
  }

  if (!isKnovioConnectionPublish) {
    return (
      <div className='m-8' data-test-id='placeholder-text'>
        <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={joinMethod?.detailsPlaceholderText ?? ''} className='description'/>
      </div>
    );
  }

  if ((state.inProgress || isDialInPending) && (isRegistrationNeeded || shouldCreatePanelistLink)) {
    if (isAudioBridgeType) {
      return (
        <div className='text-left p-8'>
          {getPassportConfiguration()}
          <div
            className={showHideButtonEnabled ? displayShowDialInInfoButton : ''}
          >

            {getSpinner('inprogress-block')}
          </div>
          {isAudioBridgeType && showHideButtonEnabled && (
            <Button
              className='connection-item__show-dial-button'
              name='toggle-dial'
              onClick={toggleDialInInfo}
            >
              {dialInfoButtonTitle}
            </Button>
          )}
        </div>
      );
    } else return getSpinner('m-8');
  }

  const uniqueDialInCode = state.uniqueDialInCode || panelistUniqueDialInCode;
  if (uniqueDialInCode) {
    joinMethod?.setUniqueDialInCode(uniqueDialInCode);
  }

  return (
    <div>
      <div className='text-left p-8'>
        {getPassportConfiguration()}
        <ConnectToMeeting {...{ ...meetingDetails, isConnectBtnDisabled, joinMethod, isMultipleConnection, joinUrl, showDialInInfo, toggleDialInInfo }} />
      </div>
      {isConnectionDetailsShown && connectionDetails && !isConnectBtnDisabled && !isRegistrationNeeded && (
        <div className='px-8 font-size-14px'>
          <div className='connection-details-title font-semibold'>Connection Details</div>
          <div className='pt-4 whitespace-pre-wrap leading-loose'>
            {connectionDetails}
          </div>
        </div>
      )}
      <Modal style={ERR_MODAL_COMMON_STYLES} isOpen={isErrorModalOpen} ariaHideApp={false}>
        <div className="modal-wrapper flex items-center flex-col">
          <p className="mb-4">
            Your system settings do not allow us to update your connection details automatically.
            Please refresh the page in a few moments to see details of how to connect to this meeting.
          </p>
          <Button
            size={'big'}
            name='ok'
            onClick={closeErrorModal}
            label='OK'
          />
        </div>
      </Modal>
    </div>
  );
};
